import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Input, Modal, PageHeader, Pill } from "@/components/ui";
import { Field, FormSection, Select, Panel, fmtBytes, fmtBps, fmtBpsShort, flag, NodeCard, HistoryPanel } from "./Nodes";
import type { Node as NodeType } from "./Nodes";
import { health } from "@/lib/health";
import { popup, confirmPopup } from "@/components/popup";

// Клик по ноде в пуле → попап с полной карточкой ноды прямо во вкладке Хосты
// (без прыжка в раздел Ноды). Модульный стор — один хост монтируется на странице.
let _openNode: ((id: number | null) => void) | null = null;
function openNode(id: number) {
  _openNode?.(id);
}

function NodeModalHost() {
  const qc = useQueryClient();
  const [nodeId, setNodeId] = useState<number | null>(null);
  useEffect(() => {
    _openNode = setNodeId;
    return () => {
      _openNode = null;
    };
  }, []);
  const q = useQuery<{ nodes: NodeType[] }>({
    queryKey: ["nodes"],
    queryFn: () => api("/api/admin/nodes"),
    enabled: nodeId != null,
    refetchInterval: nodeId != null ? 5000 : false,
  });
  const node = q.data?.nodes.find((n) => n.id === nodeId);
  if (nodeId == null) return null;
  return (
    <Modal open onClose={() => setNodeId(null)} title="Нода" maxWidth="max-w-2xl" elevated>
      {node ? (
        <NodeCard
          node={node}
          onEdit={() => popup({ tone: "info", body: "Редактирование ноды — в разделе «Ноды»." })}
          onChanged={() => qc.invalidateQueries({ queryKey: ["nodes"] })}
        />
      ) : (
        <div className="text-zinc-500 p-6 text-center text-sm">Загрузка…</div>
      )}
    </Modal>
  );
}

interface ExitSummary {
  node_id: number;
  name: string;
  host: string;
  role: string;
  engine: string;
  node_state: string;
  weight: number;
  state: string;
  pool_role: string;
  primary?: boolean;
  agent_online?: boolean;
  conns?: number | null;
  clients?: number | null;
  mem_pct?: number | null;
  cpu_pct?: number | null;
  rx_bps?: number | null;
  tx_bps?: number | null;
  health?: string;
}
interface Endpoint {
  id: number;
  name: string;
  kind: string;
  address: string;
  port: number;
  transport: string;
  cloak_domain: string | null;
  public_secret: string | null;
  ad_tag: string | null;
  entry_node_id: number | null;
  geo_allow_countries: string | null;
  max_users: number;
  is_active: boolean;
  servable: boolean;
  created_at: string;
  exits: ExitSummary[];
  active_subscriptions: number;
  sample_link: string;
  health: string;
  entry_ok: boolean;
  exit_ok: boolean;
  health_reason: string | null;
  test_link: string;
  total_conns: number;
  unique_clients: number;
  conns_per_client: number | null;
  rx_bps: number;
  tx_bps: number;
  is_ad: boolean;
}

// FakeTLS-cloak реально применяется ТОЛЬКО при транспорте faketls/doublehop
// (совпадает с ProxyService.transport_spec на бэке). На dd-транспорте cloak
// хранится, но НЕ работает — не показываем «под {cloak}», чтобы не врать.
const cloakActive = (ep: { transport: string; cloak_domain: string | null }) =>
  !!ep.cloak_domain && (ep.transport.includes("faketls") || ep.transport.includes("doublehop"));

interface NodeLite {
  id: number;
  name: string;
  host: string;
  role: string;
  engine?: string;
  country?: string | null;
  health?: string;
}

const ENGINE_SHORT: Record<string, string> = {
  mtproto_zig: "zig",
  mtproto_docker: "docker",
  mtproto_ad: "zig(ad)",
  xray_bridge: "xray",
  alexbers: "alexbers",
};

const KINDS = ["paid", "trial", "ad"];
const TRANSPORTS = ["mtproxy_doublehop", "mtproxy_faketls", "mtproxy_dd"];
const TRANSPORT_LABEL: Record<string, string> = {
  mtproxy_doublehop: "double-hop",
  mtproxy_faketls: "FakeTLS",
  mtproxy_dd: "dd (обфускация)",
};

// Цвет чипа несёт смысл: транспорты и виды хостов различаются на глаз, а не
// сливаются в один оранжевый.
const TRANSPORT_TONE: Record<string, "info" | "violet" | "cyan" | "neutral"> = {
  mtproxy_dd: "cyan",
  mtproxy_faketls: "violet",
  mtproxy_doublehop: "info",
};
const KIND_TONE: Record<string, "success" | "warning" | "pink" | "neutral"> = {
  paid: "success",
  trial: "warning",
  ad: "pink",
};

// serving/standby — внутренние коды, юзеру показываем по-русски.
const STATE_LABEL: Record<string, string> = { serving: "в работе", standby: "в резерве" };
const stateLabel = (s: string) => STATE_LABEL[s] ?? s;

function plural(n: number, forms: [string, string, string]): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return forms[0];
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return forms[1];
  return forms[2];
}

export default function Endpoints() {
  const [formOpen, setFormOpen] = useState(false);
  const [editEp, setEditEp] = useState<Endpoint | null>(null);
  const qc = useQueryClient();

  const q = useQuery<{ endpoints: Endpoint[] }>({
    queryKey: ["endpoints"],
    queryFn: () => api("/api/admin/endpoints"),
    refetchInterval: 8000,
  });
  const eps = q.data?.endpoints ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["endpoints"] });
  const [tab, setTab] = useState<"active" | "archived">("active");
  const [selectMode, setSelectMode] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Решение по подпискам удаляемых хостов: null — не спрашивали, иначе список
  // {id, name, count} из 409 — рисуем модалку «куда пересадить / в долг».
  const [decision, setDecision] = useState<{ eps: { id: number; name: string; count: number }[]; total: number } | null>(null);
  const togglePick = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const exitSelect = () => { setSelectMode(false); setPicked(new Set()); setDecision(null); };

  const runBulk = async (extra?: { migrate_to?: number; to_debt?: boolean }) => {
    const ids = [...picked];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await api<{ success: boolean; deleted: number[]; failed: { endpoint_id: number; error: string }[] }>(
        "/api/admin/endpoints/bulk-delete",
        { method: "POST", body: JSON.stringify({ endpoint_ids: ids, ...extra }) },
      );
      if (r.failed?.length) {
        popup({ tone: "error", title: "Удалено не всё", body: `Удалено ${r.deleted.length}. Не удалось: ${r.failed.map((f) => `#${f.endpoint_id} — ${f.error}`).join("; ")}` });
      } else {
        popup({ tone: "success", title: "Готово", body: `Удалено хостов: ${r.deleted.length}` });
      }
      exitSelect();
      refresh();
    } catch (e: any) {
      const d = e?.detail ?? e?.body?.detail;
      if (d?.reason === "has_active_subscriptions") {
        setDecision({ eps: d.endpoints ?? [], total: d.total ?? 0 });
      } else {
        popup({ tone: "error", title: "Ошибка", body: String(e?.message ?? e) });
      }
    } finally {
      setBulkBusy(false);
    }
  };

  const doBulkDelete = async () => {
    const ids = [...picked];
    if (!ids.length) return;
    const names = eps.filter((e) => picked.has(e.id)).map((e) => e.name);
    if (!(await confirmPopup({
      title: `Удалить ${ids.length} ${ids.length === 1 ? "хост" : "хоста(ов)"}?`,
      body: `${names.slice(0, 8).join(", ")}${names.length > 8 ? ` и ещё ${names.length - 8}` : ""}. Движки снесутся, DNS снимется. Если есть активные подписки — спросим, куда их деть.`,
      danger: true,
      confirmLabel: "Удалить",
    }))) return;
    await runBulk();
  };
  const activeEps = eps.filter((e) => e.is_active);
  const archivedEps = eps.filter((e) => !e.is_active);
  // Сортировка: при десятке хостов плоский список по id нечитаем.
  const [sortBy, setSortBy] = useState<"kind" | "name" | "subs" | "transport">("kind");
  const KIND_RANK: Record<string, number> = { paid: 0, trial: 1, ad: 2 };
  const shown = [...(tab === "active" ? activeEps : archivedEps)].sort((a, b) => {
    if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "subs") return (b.active_subscriptions ?? 0) - (a.active_subscriptions ?? 0);
    if (sortBy === "transport") return (a.transport || "").localeCompare(b.transport || "");
    return (KIND_RANK[a.kind] ?? 9) - (KIND_RANK[b.kind] ?? 9)
        || (a.name || "").localeCompare(b.name || "");
  });

  return (
    <div className="p-4 md:p-8">
      <NodeModalHost />
      <PageHeader
        title="Хосты"
        right={
          <Button
            variant="primary"
            onClick={() => {
              setEditEp(null);
              setFormOpen(true);
            }}
          >
            + Хост
          </Button>
        }
      />

      <p className="text-sm text-zinc-500 max-w-3xl mb-5">
        Хост — точка входа для клиента: <b className="text-zinc-300">домен</b> (что видит юзер) +
        маршрут за ним. Один домен держит пул выходов — их можно свапать без смены ссылки у юзера.
      </p>

      <div className="flex gap-2 mb-4 items-center flex-wrap">
        {(["active", "archived"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              tab === t ? "bg-brand/20 text-brand" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t === "active" ? `Активные · ${activeEps.length}` : `📦 Архив · ${archivedEps.length}`}
          </button>
        ))}
        <div className="flex-1" />
        {!selectMode ? (
          <Button variant="ghost" onClick={() => setSelectMode(true)}>Выделить</Button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">{picked.size ? `выбрано ${picked.size}` : "клик по карточке — выбрать"}</span>
            <Button variant="danger" disabled={bulkBusy || !picked.size} onClick={doBulkDelete}>
              {bulkBusy ? "Удаляю…" : `Удалить${picked.size ? ` (${picked.size})` : ""}`}
            </Button>
            <Button variant="ghost" onClick={exitSelect}>Отмена</Button>
          </div>
        )}
      </div>

      {shown.length > 2 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap text-sm">
          <span className="text-zinc-600 text-xs">сортировать</span>
          {([["kind","по типу"],["name","по имени"],["subs","по подпискам"],["transport","по транспорту"]] as const).map(([k,label]) => (
            <button
              key={k}
              onClick={() => setSortBy(k)}
              className={`h-7 px-2.5 rounded-lg text-xs transition-colors ${
                sortBy === k ? "bg-brand-soft text-brand" : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {!q.isLoading && shown.length === 0 && (
        <div className="text-zinc-500">
          {tab === "active" ? "Активных хостов нет." : "Архив пуст."}
        </div>
      )}

      <div className="columns-1 xl:columns-2 gap-4">
        {shown.map((ep) => (
          <div key={ep.id} className="relative break-inside-avoid mb-4">
            {selectMode && (
              <button
                onClick={() => togglePick(ep.id)}
                className={`absolute -left-2 -top-2 z-10 w-7 h-7 rounded-lg border flex items-center justify-center text-sm transition ${
                  picked.has(ep.id) ? "bg-brand text-black border-brand" : "bg-zinc-900 border-white/20 text-zinc-500 hover:border-brand"
                }`}
              >
                {picked.has(ep.id) ? "✓" : ""}
              </button>
            )}
            <div className={selectMode && picked.has(ep.id) ? "ring-1 ring-brand rounded-2xl" : ""}>
              <HostCard ep={ep} onEdit={() => { setEditEp(ep); setFormOpen(true); }} onChanged={refresh} />
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!decision} onClose={() => setDecision(null)} title="Куда деть подписки?">
        {decision && (
          <SubsDecision
            eps={decision.eps}
            total={decision.total}
            excludeIds={[...picked]}
            busy={bulkBusy}
            onMigrate={(target) => runBulk({ migrate_to: target })}
            onDebt={() => runBulk({ to_debt: true })}
            onCancel={() => setDecision(null)}
          />
        )}
      </Modal>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editEp ? `Хост · ${editEp.name}` : "Новый хост"}
        maxWidth="max-w-2xl"
      >
        <HostForm
          ep={editEp}
          onDone={() => {
            setFormOpen(false);
            refresh();
          }}
          onCancel={() => setFormOpen(false)}
        />
      </Modal>
    </div>
  );
}

function HostCard({
  ep,
  onEdit,
  onChanged,
}: {
  ep: Endpoint;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachRole, setAttachRole] = useState<"entry" | "exit">("exit");
  const [menu, setMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [singleDecision, setSingleDecision] = useState<{ count: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as any)) setMenu(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menu]);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/api/admin/endpoints/${ep.id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: onChanged,
  });
  const swap = useMutation({
    mutationFn: (v: { node_id: number; state: string }) =>
      api(`/api/admin/endpoints/${ep.id}/exits/state`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: onChanged,
  });
  const detach = useMutation({
    mutationFn: (node_id: number) =>
      api(`/api/admin/endpoints/${ep.id}/exits/${node_id}`, { method: "DELETE" }),
    onSuccess: onChanged,
  });
  const del = useMutation({
    mutationFn: (opts?: boolean | { migrate_to?: number; to_debt?: boolean }) => {
      const p = new URLSearchParams();
      if (opts === true) p.set("force", "true");
      if (typeof opts === "object" && opts?.migrate_to) p.set("migrate_to", String(opts.migrate_to));
      if (typeof opts === "object" && opts?.to_debt) p.set("to_debt", "true");
      const qs = p.toString();
      return api(`/api/admin/endpoints/${ep.id}${qs ? `?${qs}` : ""}`, { method: "DELETE" });
    },
    onSuccess: onChanged,
  });
  const archive = useMutation({
    mutationFn: () => api(`/api/admin/endpoints/${ep.id}/archive`, { method: "POST" }),
    onSuccess: onChanged,
  });
  const restore = useMutation({
    mutationFn: () => api(`/api/admin/endpoints/${ep.id}/restore`, { method: "POST" }),
    onSuccess: onChanged,
  });
  const cfSync = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; error?: string; action?: string; ip?: string }>(
        `/api/admin/endpoints/${ep.id}/cf-sync`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      if (r.ok) popup({ tone: "success", title: "Домен привязан", body: `${ep.address} → ${r.ip} (${r.action})` });
      else popup({ tone: "error", title: "Cloudflare", body: r.error ?? "не удалось" });
      onChanged();
    },
    onError: (e) => popup({ tone: "error", title: "Cloudflare", body: (e as Error).message }),
  });

  interface DeployStep { node?: string; role?: string; success?: boolean; error?: string; forwards_to?: string }
  interface DeployRes { ok: boolean; error?: string; exits: number; entries: number; steps: DeployStep[] }
  const fmtDeploy = (r: DeployRes) =>
    r.steps
      .map((s) => `${s.success ? "✅" : "❌"} ${s.role ?? ""} ${s.node ?? ""}` +
        (s.forwards_to ? ` → ${s.forwards_to}` : "") + (s.error ? ` — ${s.error}` : ""))
      .join("\n");
  const deploy = useMutation({
    mutationFn: () => api<DeployRes>(`/api/admin/endpoints/${ep.id}/deploy`, { method: "POST" }),
    onSuccess: (r) => {
      popup({ tone: r.ok ? "success" : "error", title: r.ok ? "Каскад развёрнут" : "Ошибка деплоя",
        body: (r.error ? r.error + "\n\n" : "") + fmtDeploy(r) });
      onChanged();
    },
    onError: (e) => popup({ tone: "error", title: "Деплой", body: (e as Error).message }),
  });
  const syncUsers = useMutation({
    mutationFn: () => api<DeployRes>(`/api/admin/endpoints/${ep.id}/sync-users`, { method: "POST" }),
    onSuccess: (r) => popup({ tone: r.ok ? "success" : "error", title: r.ok ? "Секреты перезалиты" : "Ошибка", body: fmtDeploy(r) }),
    onError: (e) => popup({ tone: "error", title: "Секреты", body: (e as Error).message }),
  });
  const exitLinks = ep.exits.filter((e) => (e.pool_role || "exit") === "exit");
  const entryLinks = ep.exits.filter((e) => e.pool_role === "entry");
  const serving = exitLinks.filter((e) => e.state === "serving");
  const isDouble = ep.transport === "mtproxy_doublehop";
  const hs = health(ep.health);

  return (
    <div className={`rounded-2xl card-elev p-4 flex flex-col ring-1 ring-inset ${hs.ring}`}>
      {/* header */}
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${hs.dot}`} title={hs.label} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setDetailOpen(true)} className="font-mono font-semibold text-zinc-100 hover:text-brand transition-colors truncate" title="детали хоста">
              {ep.address}:{ep.port}
            </button>
            <span className={`text-xs font-medium ${hs.text}`}>
              {hs.label}{ep.health_reason ? ` · ${ep.health_reason}` : ""}
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
            {ep.name} · <Pill tone={KIND_TONE[ep.kind] ?? "neutral"}>{ep.kind}</Pill>{" "}
            <Pill tone={TRANSPORT_TONE[ep.transport] ?? "neutral"}>{TRANSPORT_LABEL[ep.transport] ?? ep.transport}</Pill>
            {!ep.servable && ep.is_active && (
              <span
                className="text-[11px] px-2 py-0.5 rounded-lg ring-1 ring-inset text-amber-300 bg-amber-500/10 ring-amber-500/25"
                title="хост поднят, но не участвует в авто-выдаче новым юзерам"
              >
                🚫 не выдаётся
              </span>
            )}
            {isDouble && (
              <span className="ml-1">
                вход <span className={ep.entry_ok ? "text-emerald-400" : "text-rose-400"}>{ep.entry_ok ? "✓" : "✗"}</span>
                {" · "}выход <span className={ep.exit_ok ? "text-emerald-400" : "text-rose-400"}>{ep.exit_ok ? "✓" : "✗"}</span>
              </span>
            )}
          </div>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenu((v) => !v)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-100 hover:bg-white/[0.06] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
            </svg>
          </button>
          {menu && (
            <div className="absolute right-0 top-9 z-20 min-w-[190px] rounded-xl card-elev shadow-pop py-1 text-sm">
              <MenuItem onClick={() => { setMenu(false); onEdit(); }}>Редактировать</MenuItem>
              <MenuItem
                onClick={async () => {
                  setMenu(false);
                  if (await confirmPopup({
                    title: ep.health === "up" ? "Пересобрать хост?" : "Развернуть хост?",
                    body: `«${ep.name}»: поднять движки + залить секреты всех подписок на serving-выходы, пересобрать вход и DNS. Идемпотентно.`,
                    confirmLabel: ep.health === "up" ? "Пересобрать" : "Развернуть",
                  }))
                    deploy.mutate();
                }}
              >
                {deploy.isPending ? "…" : ep.health === "up" ? "🔄 Пересобрать" : "🚀 Развернуть"}
              </MenuItem>
              <MenuItem onClick={() => { setMenu(false); setMigrateOpen(true); }}>
                🔀 Пересадить подписки
              </MenuItem>
              <MenuItem onClick={() => { setMenu(false); navigator.clipboard?.writeText(ep.sample_link); }}>
                Скопировать ссылку
              </MenuItem>
              <MenuItem onClick={() => { setMenu(false); patch.mutate({ servable: !ep.servable }); }}>
                {ep.servable ? "🚫 Убрать из выдачи" : "🎁 Вернуть в выдачу"}
              </MenuItem>
              <MenuItem onClick={() => { setMenu(false); patch.mutate({ is_active: !ep.is_active }); }}>
                {ep.is_active ? "Выключить" : "Включить"}
              </MenuItem>
              <div className="my-1 border-t border-white/[0.06]" />
              {ep.is_active ? (
                <MenuItem onClick={async () => { setMenu(false); if (await confirmPopup({ title: "В архив?", body: `${ep.name}: движки глушим, но хост и подписки сохраняются — вернёшь одним кликом, ссылки оживут.`, confirmLabel: "В архив" })) archive.mutate(); }}>
                  📦 В архив
                </MenuItem>
              ) : (
                <MenuItem onClick={() => { setMenu(false); restore.mutate(); }}>
                  ♻️ Восстановить из архива
                </MenuItem>
              )}
              <MenuItem danger onClick={async () => {
                setMenu(false);
                if (!(await confirmPopup({ title: "Удалить хост НАВСЕГДА?", body: `${ep.name}: движок снесётся, DNS снимется. Если есть активные подписки — спросим, куда их деть.`, danger: true, confirmLabel: "Удалить навсегда" }))) return;
                try { await del.mutateAsync(undefined); }
                catch (e: any) {
                  const d = e?.detail ?? e?.body?.detail;
                  if (d?.reason === "has_active_subscriptions") {
                    setSingleDecision({ count: d.count ?? 0 });
                  } else popup({ tone: "error", title: "Удаление", body: String(e?.message ?? e) });
                }
              }}>
                Удалить навсегда
              </MenuItem>
            </div>
          )}
        </div>
      </div>

      {/* route visual */}
      <div className="flex items-center gap-1.5 flex-wrap mt-4 text-xs">
        <RouteNode icon="👤" label="Клиент" />
        <Arrow />
        <RouteNode icon="🌐" label={ep.address} tone="brand" sub={cloakActive(ep) ? `под ${ep.cloak_domain}` : undefined} />
        {isDouble && (
          <>
            <Arrow />
            <RouteNode
              icon="🌉"
              label={`${entryLinks.length} ${plural(entryLinks.length, ["мост", "моста", "мостов"])}`}
              tone="amber"
            />
          </>
        )}
        <Arrow />
        <RouteNode
          icon="🚀"
          label={`${serving.length} ${plural(serving.length, ["выход", "выхода", "выходов"])}`}
          tone="emerald"
        />
        <Arrow />
        <RouteNode icon="✈️" label="Telegram" />
      </div>

      {/* meta + live */}
      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500 flex-wrap">
        <span title="уникальные IP на входе ≈ реальные люди/устройства">
          клиентов: <b className="text-emerald-300 tabular-nums">{ep.unique_clients}</b>
        </span>
        <span title="коннектов на клиента (норма ~4-10; много = скан/абуз)">
          конн/клиент: <b className={(ep.conns_per_client ?? 0) >= 40 ? "text-rose-300 tabular-nums" : "text-zinc-300 tabular-nums"}>
            {ep.conns_per_client ?? "—"}</b>
        </span>
        <span title="суммарно живых коннектов">
          коннектов: <b className="text-zinc-400 tabular-nums">{ep.total_conns}</b>
        </span>
        <span title="суммарный трафик по нодам">
          <b className="text-emerald-300">↓{fmtBpsShort(ep.rx_bps)}</b> <b className="text-sky-300">↑{fmtBpsShort(ep.tx_bps)}</b>
        </span>
        {!ep.is_ad && (
          <span>подписок: <b className="text-zinc-300 tabular-nums">{ep.active_subscriptions}</b></span>
        )}
        <span title="агентов онлайн в пуле">
          агентов: <b className={ep.exits.filter((e) => e.agent_online).length ? "text-brand" : "text-zinc-400"}>
            {ep.exits.filter((e) => e.agent_online).length}</b>/{ep.exits.length}
        </span>
        <DomainBadge endpointId={ep.id} address={ep.address} onBind={() => cfSync.mutate()} />
        {ep.kind === "ad" && (
          <span className={ep.ad_tag ? "text-amber-300/90" : "text-rose-300/90"} title="спонсорский тег @MTProxybot">
            📣 тег: {ep.ad_tag ? `${ep.ad_tag.slice(0, 6)}…` : "не задан"}
          </span>
        )}
        {ep.test_link ? (
          <button
            onClick={() => { navigator.clipboard?.writeText(ep.test_link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            className="ml-auto text-brand hover:underline"
            title={ep.test_link}
          >
            {copied ? "✓ скопировано" : "📋 рабочая тест-ссылка"}
          </button>
        ) : (
          <span className="ml-auto text-zinc-600" title="нет serving-выхода с admin-секретом — разверни каскад">
            тест-ссылка недоступна
          </span>
        )}
      </div>

      {isDouble && (
        <PoolBlock
          title="Пул мостов · вход (РФ)"
          links={entryLinks}
          empty="нет мостов — привяжите entry-ноду"
          onAttach={() => { setAttachRole("entry"); setAttachOpen(true); }}
          onSwap={(node_id, state) => swap.mutate({ node_id, state })}
          onDetach={(node_id) => detach.mutate(node_id)}
        />
      )}
      <PoolBlock
        title="Пул выходов"
        links={exitLinks}
        empty="пул пуст — привяжите exit-ноду"
        onAttach={() => { setAttachRole("exit"); setAttachOpen(true); }}
        onSwap={(node_id, state) => swap.mutate({ node_id, state })}
        onDetach={(node_id) => detach.mutate(node_id)}
      />

      <Modal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        title={attachRole === "entry" ? "Привязать мост" : "Привязать выход"}
      >
        <AttachExitForm
          endpointId={ep.id}
          role={attachRole}
          onDone={() => { setAttachOpen(false); onChanged(); }}
        />
      </Modal>

      <HostDetail
        ep={ep}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onDeploy={() => deploy.mutate()}
        onSync={() => syncUsers.mutate()}
        onEdit={onEdit}
        deploying={deploy.isPending}
        syncing={syncUsers.isPending}
      />

      <Modal open={!!singleDecision} onClose={() => setSingleDecision(null)} title="Куда деть подписки?">
        {singleDecision && (
          <SubsDecision
            eps={[{ id: ep.id, name: ep.name, count: singleDecision.count }]}
            total={singleDecision.count}
            excludeIds={[ep.id]}
            busy={del.isPending}
            onMigrate={async (target) => {
              try { await del.mutateAsync({ migrate_to: target }); setSingleDecision(null); onChanged(); }
              catch (e: any) { popup({ tone: "error", title: "Удаление", body: String(e?.message ?? e) }); }
            }}
            onDebt={async () => {
              try { await del.mutateAsync({ to_debt: true }); setSingleDecision(null); onChanged(); }
              catch (e: any) { popup({ tone: "error", title: "Удаление", body: String(e?.message ?? e) }); }
            }}
            onCancel={() => setSingleDecision(null)}
          />
        )}
      </Modal>

      <Modal open={migrateOpen} onClose={() => setMigrateOpen(false)} title={`Пересадить подписки · ${ep.name}`}>
        <MigrateModal ep={ep} onDone={() => { setMigrateOpen(false); onChanged(); }} onCancel={() => setMigrateOpen(false)} />
      </Modal>
    </div>
  );
}

interface ClientSource { ip: string; count: number; country?: string }
interface ClientsData {
  available: boolean;
  reason?: string;
  unique_clients?: number;
  total_conns?: number;
  conns_per_client?: number | null;
  by_country?: { country: string; count: number }[];
  sources?: ClientSource[];
  sources_truncated?: boolean;
  from_role?: string;
}
function ClientsPanel({ endpointId }: { endpointId: number }) {
  const [showIps, setShowIps] = useState(false);
  const q = useQuery<ClientsData>({
    queryKey: ["endpoint-clients", endpointId],
    queryFn: () => api(`/api/admin/endpoints/${endpointId}/clients`),
    refetchInterval: 10000,
  });
  const d = q.data;
  const geo = d?.by_country ?? [];
  const geoTotal = geo.reduce((a, c) => a + c.count, 0) || 1;
  const sources = d?.sources ?? [];
  return (
    <Panel title="Клиенты" icon="👤" badge={d?.from_role ? `со стороны: ${d.from_role}` : undefined}>
      {!d ? (
        <div className="text-sm text-zinc-500">Загрузка…</div>
      ) : !d.available ? (
        <div className="text-sm text-zinc-500">{d.reason || "Нет данных о клиентах"}</div>
      ) : (
        <div className="space-y-3">
          {/* крупные агрегаты */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-1.5">
              <div className="text-[10px] uppercase text-zinc-600">уник. клиентов</div>
              <div className="text-lg font-bold text-emerald-300 tabular-nums">{d.unique_clients}</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-1.5">
              <div className="text-[10px] uppercase text-zinc-600">коннектов</div>
              <div className="text-lg font-bold text-zinc-200 tabular-nums">{d.total_conns}</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-1.5">
              <div className="text-[10px] uppercase text-zinc-600">на клиента</div>
              <div className={`text-lg font-bold tabular-nums ${(d.conns_per_client ?? 0) >= 40 ? "text-rose-400" : "text-zinc-200"}`}>{d.conns_per_client ?? "—"}</div>
            </div>
          </div>

          {/* гео-бары (масштабируется на любое кол-во стран) */}
          {geo.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-zinc-600">гео клиентов</div>
              {geo.slice(0, 10).map((c) => {
                const pct = Math.round((c.count / geoTotal) * 100);
                return (
                  <div key={c.country} className="flex items-center gap-2 text-xs">
                    <span className="w-14 shrink-0 text-zinc-300">{flag(c.country)} {c.country}</span>
                    <div className="flex-1 h-2 rounded-full bg-white/[0.04] overflow-hidden">
                      <div className="h-full bg-brand-grad" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-16 text-right tabular-nums text-zinc-500">{c.count} · {pct}%</span>
                  </div>
                );
              })}
              {geo.length > 10 && <div className="text-[10px] text-zinc-600">+ ещё {geo.length - 10} стран</div>}
            </div>
          )}

          {/* список IP — сворачиваемый, чтобы не раздувать карточку */}
          {sources.length > 0 && (
            <div>
              <button onClick={() => setShowIps((v) => !v)} className="text-xs text-brand hover:underline">
                {showIps ? "▾ скрыть IP" : `▸ топ IP (${sources.length}${d.sources_truncated ? " из многих" : ""})`}
              </button>
              {showIps && (
                <div className="mt-1.5 rounded-lg border border-white/[0.06] overflow-hidden max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#141922] text-[10px] uppercase text-zinc-500">
                      <tr><th className="text-left px-3 py-1.5">IP</th><th className="text-left px-3 py-1.5">гео</th><th className="text-right px-3 py-1.5">конн</th></tr>
                    </thead>
                    <tbody>
                      {sources.map((s) => (
                        <tr key={s.ip} className="border-t border-white/[0.04]">
                          <td className="px-3 py-1 font-mono text-zinc-300">{s.ip}</td>
                          <td className="px-3 py-1">{s.country ? `${flag(s.country)} ${s.country}` : "—"}</td>
                          <td className="px-3 py-1 text-right tabular-nums text-zinc-400">{s.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {d.sources_truncated && <div className="text-[10px] text-zinc-600 px-3 py-1.5">показаны топ-30 по коннектам из {d.unique_clients} клиентов</div>}
                </div>
              )}
            </div>
          )}
          {sources.length === 0 && <div className="text-xs text-zinc-600">Активных клиентов сейчас нет.</div>}
        </div>
      )}
    </Panel>
  );
}

function GeoPanel({ ep, onChanged }: { ep: Endpoint; onChanged: () => void }) {
  const q = useQuery<{ available: boolean; reason?: string; node?: string; state?: string; active?: boolean; cidrs?: number; rule?: boolean; drops?: number; countries?: string | null }>({
    queryKey: ["endpoint-geo", ep.id],
    queryFn: () => api(`/api/admin/endpoints/${ep.id}/geo-status`),
    refetchInterval: 15000,
  });
  const act = useMutation({
    mutationFn: (action: "apply" | "clear") =>
      api<{ ok: boolean; results?: { node?: string; ok?: boolean; error?: string }[]; error?: string }>(
        `/api/admin/endpoints/${ep.id}/geo/${action}`,
        { method: "POST" },
      ),
    onSuccess: (r, action) => {
      popup({
        tone: r.ok ? "success" : "warning",
        title: action === "apply" ? "Гео применён" : "Гео снят",
        body: r.error || (r.results ?? []).map((x) => `${x.ok ? "✅" : "❌"} ${x.node}${x.error ? " — " + x.error : ""}`).join("\n") || "готово",
      });
      q.refetch();
      onChanged();
    },
    onError: (e) => popup({ tone: "error", title: "Гео", body: (e as Error).message }),
  });
  const d = q.data;
  const countries = ep.geo_allow_countries;

  return (
    <Panel title="Гео-фильтр" icon="🌍" badge={d?.node ? `вход: ${d.node}` : undefined}>
      <div className="space-y-2.5">
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <span className="text-zinc-500">разрешены:</span>
          {countries ? (
            <span className="font-mono text-zinc-200">{countries}</span>
          ) : (
            <span className="text-zinc-500">не заданы (фильтр выключен) — задай в ⚙ Настройки</span>
          )}
        </div>
        {d && d.available && (
          d.active ? (
            <div className="flex items-center gap-4 text-xs">
              <span className={d.rule ? "text-emerald-300" : "text-rose-300"}>
                {d.rule ? "✓ активен" : "⚠ набор есть, правило НЕ стоит"}
              </span>
              <span className="text-zinc-400">CIDR: <b className="tabular-nums">{d.cidrs}</b></span>
              <span className="text-zinc-400">зарезано: <b className="tabular-nums">{d.drops}</b></span>
            </div>
          ) : (
            <div className="text-xs text-amber-300/90">не применён (state: {d.state})</div>
          )
        )}
        {d && !d.available && <div className="text-xs text-zinc-500">{d.reason}</div>}
        <div className="flex gap-2">
          <Button variant="primary" disabled={!countries || act.isPending} onClick={() => act.mutate("apply")}>
            {act.isPending ? "…" : "Применить"}
          </Button>
          <Button variant="ghost" disabled={act.isPending} onClick={() => act.mutate("clear")}>
            Снять
          </Button>
          <Button variant="ghost" disabled={q.isFetching} onClick={() => q.refetch()}>
            {q.isFetching ? "…" : "↻ Проверить"}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function DomainBadge({ endpointId, address, onBind }: { endpointId: number; address: string; onBind: () => void }) {
  const q = useQuery<{ is_domain: boolean; bound?: boolean; matches?: boolean; dns_ip?: string | null; entry_ip?: string | null }>({
    queryKey: ["domain-status", endpointId],
    queryFn: () => api(`/api/admin/endpoints/${endpointId}/domain-status`),
    refetchInterval: 20000,
  });
  const d = q.data;
  if (!d || !d.is_domain) return null;
  if (d.matches) return <span className="text-emerald-400/90" title={`${address} → ${d.dns_ip}`}>🌐 привязан</span>;
  if (d.bound)
    return (
      <button onClick={onBind} className="text-amber-300 hover:underline" title={`резолвится на ${d.dns_ip}, мост ${d.entry_ip ?? "?"}`}>
        🌐 не туда → привязать
      </button>
    );
  return (
    <button onClick={onBind} className="text-rose-300 hover:underline" title="A-запись отсутствует">
      🌐 не привязан → привязать
    </button>
  );
}

function SubsDecision({
  eps, total, excludeIds, busy, onMigrate, onDebt, onCancel,
}: {
  eps: { id: number; name: string; count: number }[];
  total: number;
  excludeIds: number[];
  busy: boolean;
  onMigrate: (target: number) => void;
  onDebt: () => void;
  onCancel: () => void;
}) {
  const [target, setTarget] = useState<number | null>(null);
  const q = useQuery<{ endpoints: Endpoint[] }>({ queryKey: ["endpoints"], queryFn: () => api("/api/admin/endpoints") });
  const candidates = (q.data?.endpoints ?? []).filter(
    (e) => e.is_active && !excludeIds.includes(e.id),
  );
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-400 leading-relaxed">
        На удаляемых хостах <b className="text-zinc-200">{total}</b> активных подписок:
      </p>
      <div className="text-sm text-zinc-300 space-y-1">
        {eps.map((e) => (
          <div key={e.id} className="flex justify-between">
            <span>{e.name}</span>
            <span className="text-zinc-500">{e.count} подписок</span>
          </div>
        ))}
      </div>
      <div>
        <div className="text-xs text-zinc-500 mb-2">Пересадить на хост (юзерам уйдёт новая ссылка):</div>
        <div className="flex flex-wrap gap-2">
          {candidates.map((e) => (
            <button
              key={e.id}
              onClick={() => setTarget(e.id)}
              className={`h-8 px-3 rounded-lg text-sm transition ${
                target === e.id ? "bg-brand-soft text-brand glow-ring" : "bg-white/[0.04] text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {e.name} · {e.kind}
            </button>
          ))}
          {candidates.length === 0 && <span className="text-zinc-600 text-sm">живых хостов-кандидатов нет</span>}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Отмена</Button>
        <Button variant="ghost" onClick={onDebt} disabled={busy}>
          В долг (выдадим позже)
        </Button>
        <Button variant="primary" disabled={!target || busy} onClick={() => target && onMigrate(target)}>
          {busy ? "…" : "Пересадить и удалить"}
        </Button>
      </div>
    </div>
  );
}


function MigrateModal({ ep, onDone, onCancel }: { ep: Endpoint; onDone: () => void; onCancel: () => void }) {
  const [target, setTarget] = useState<number | null>(null);
  const q = useQuery<{ endpoints: Endpoint[] }>({ queryKey: ["endpoints"], queryFn: () => api("/api/admin/endpoints") });
  const others = (q.data?.endpoints ?? []).filter((e) => e.id !== ep.id && e.kind === ep.kind);
  const migrate = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; moved: number; synced: boolean; notified: number }>(
        `/api/admin/endpoints/${ep.id}/migrate-subs`,
        { method: "POST", body: JSON.stringify({ to_endpoint_id: target }) },
      ),
    onSuccess: (r) => {
      popup({
        tone: r.ok ? "success" : "warning",
        title: r.ok ? "Подписки пересажены" : "Пересадка с оговорками",
        body: `Перенесено: ${r.moved}\nСекреты залиты: ${r.synced ? "да" : "нет"}\nУведомлено юзеров: ${r.notified}`,
      });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "Пересадка", body: (e as Error).message }),
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-400 leading-relaxed">
        Все активные proxy-подписки хоста <b className="text-zinc-200">{ep.name}</b> (
        {ep.active_subscriptions} шт.) переедут на выбранный хост. Секреты зальются на его
        serving-выход, юзерам уйдёт уведомление с новой ссылкой (старая перестанет работать).
      </p>
      {others.length === 0 ? (
        <p className="text-xs text-amber-400/80">Нет другого хоста того же типа ({ep.kind}) для пересадки.</p>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {others.map((e) => {
            const on = target === e.id;
            const hs = health(e.health);
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => setTarget(e.id)}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition-all ${on ? "bg-brand-soft text-brand border-brand/40 glow-ring" : "bg-black/20 border-white/[0.08] text-zinc-300 hover:bg-white/[0.04]"}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${hs.dot}`} />
                  <span className="font-mono">{e.address}</span>
                  <span className="text-xs text-zinc-500">· {e.name}</span>
                  <span className={`ml-auto text-xs ${hs.text}`}>{hs.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <div className="flex justify-end gap-3 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={migrate.isPending}>Отмена</Button>
        <Button variant="primary" disabled={!target || migrate.isPending} onClick={() => migrate.mutate()}>
          {migrate.isPending ? "Пересаживаю…" : "Пересадить"}
        </Button>
      </div>
    </div>
  );
}

interface SubRow {
  id: number;
  user_id: number;
  plan: string;
  device_limit: number;
  is_active: boolean;
  expires_at: string | null;
}

function HostDetail({
  ep: epProp, open, onClose, onDeploy, onSync, onEdit, deploying, syncing,
}: {
  ep: Endpoint;
  open: boolean;
  onClose: () => void;
  onDeploy: () => void;
  onSync: () => void;
  onEdit: () => void;
  deploying: boolean;
  syncing: boolean;
}) {
  // Реалтайм: свежий хост каждые 3с, метрики нод каждые 4с (трафик агрегат).
  const q = useQuery<Endpoint>({
    queryKey: ["endpoint", epProp.id],
    queryFn: () => api(`/api/admin/endpoints/${epProp.id}`),
    enabled: open,
    refetchInterval: open ? 3000 : false,
    initialData: epProp,
  });
  const ep = q.data ?? epProp;
  const hs = health(ep.health);
  const nodeIds = ep.exits.map((e) => e.node_id);
  const nm = useQuery<(NodeMetricsLite | null)[]>({
    queryKey: ["host-node-metrics", ep.id, nodeIds.join(",")],
    queryFn: () => Promise.all(nodeIds.map((id) => api<NodeMetricsLite>(`/api/admin/nodes/${id}/metrics`).catch(() => null))),
    enabled: open && nodeIds.length > 0,
    refetchInterval: open ? 4000 : false,
  });
  const mets = (nm.data ?? []).filter(Boolean) as NodeMetricsLite[];
  const sum = (f: (s: NodeMetricsLite["system"]) => number | undefined) =>
    mets.reduce((a, m) => a + (f(m.system ?? {}) ?? 0), 0);
  const rxBps = sum((s) => s?.rx_bps), txBps = sum((s) => s?.tx_bps);
  const rxTot = sum((s) => s?.rx_total), txTot = sum((s) => s?.tx_total);

  const subs = useQuery<{ subscriptions: SubRow[] }>({
    queryKey: ["endpoint-subs", ep.id],
    queryFn: () => api(`/api/admin/subscriptions?endpoint_id=${ep.id}`),
    enabled: open,
    refetchInterval: open ? 6000 : false,
  });
  const swap = useMutation({
    mutationFn: (v: { node_id: number; state: string }) =>
      api(`/api/admin/endpoints/${ep.id}/exits/state`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => q.refetch(),
  });
  const [copied, setCopied] = useState(false);
  const [subQ, setSubQ] = useState("");
  const [subPlan, setSubPlan] = useState<"all" | "paid" | "trial">("all");
  const listAll = (subs.data?.subscriptions ?? []).filter((s) => s.is_active);
  const list = listAll.filter(
    (s) =>
      (subPlan === "all" || s.plan === subPlan) &&
      (!subQ.trim() || String(s.user_id).includes(subQ.trim())),
  );
  const entryLinks = ep.exits.filter((e) => e.pool_role === "entry");
  const exitLinks = ep.exits.filter((e) => (e.pool_role || "exit") === "exit");
  const isDouble = ep.transport === "mtproxy_doublehop";
  const deployed = ep.health === "up";
  // Нода для графика истории: боевой выход (там трафик/коннекты), иначе первый.
  const histNodeId =
    (exitLinks.find((e) => e.state === "serving") ?? exitLinks[0] ??
     entryLinks.find((e) => e.state === "serving") ?? entryLinks[0])?.node_id ?? null;

  return (
    <Modal open={open} onClose={onClose} title={`Хост · ${ep.name}`} maxWidth="max-w-4xl">
      <div className="space-y-3">
        {/* top strip */}
        <div className="flex items-center gap-2.5 flex-wrap text-sm">
          <span className={`flex items-center gap-1.5 font-medium ${hs.text}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${hs.dot}`} />{hs.label}
          </span>
          {ep.health_reason && <span className="text-xs text-zinc-500">· {ep.health_reason}</span>}
          <Pill tone={KIND_TONE[ep.kind] ?? "neutral"}>{ep.kind}</Pill>
          <Pill tone={TRANSPORT_TONE[ep.transport] ?? "neutral"}>{TRANSPORT_LABEL[ep.transport] ?? ep.transport}</Pill>
          <span className="font-mono text-xs text-zinc-500">{ep.address}:{ep.port}</span>
          <button onClick={() => { onClose(); onEdit(); }} className="ml-auto h-7 px-2.5 text-xs rounded-lg border border-white/[0.08] text-zinc-300 hover:bg-white/[0.05]">
            ⚙ Настройки
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* LEFT: обзор + маршрут + подписки */}
          <div className="space-y-3">
            <Panel title="Обзор" icon="📊">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2" title="уник. IP на входе ≈ реальные люди">
                  <div className="text-[10px] uppercase text-zinc-600">клиенты</div>
                  <div className="text-lg font-bold text-emerald-300 tabular-nums">{ep.unique_clients}</div>
                </div>
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2" title="коннектов всего / на клиента">
                  <div className="text-[10px] uppercase text-zinc-600">коннекты</div>
                  <div className="text-lg font-bold text-brand tabular-nums">{ep.total_conns}</div>
                  <div className={`text-[10px] tabular-nums ${(ep.conns_per_client ?? 0) >= 40 ? "text-rose-400" : "text-zinc-500"}`}>
                    {ep.conns_per_client ?? "—"} /клиент
                  </div>
                </div>
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2">
                  <div className="text-[10px] uppercase text-zinc-600">{ep.is_ad ? "трафик" : "выходов"}</div>
                  {ep.is_ad ? (
                    <div className="text-sm font-bold text-emerald-300 tabular-nums">↓{fmtBpsShort(ep.rx_bps)}</div>
                  ) : (
                    <div className="text-lg font-bold text-emerald-300 tabular-nums">{exitLinks.filter((e) => e.state === "serving").length}/{exitLinks.length}</div>
                  )}
                </div>
              </div>
              {isDouble && (
                <div className="text-xs text-zinc-500 flex gap-4 mb-3">
                  <span>вход <span className={ep.entry_ok ? "text-emerald-400" : "text-rose-400"}>{ep.entry_ok ? "✓" : "✗"}</span></span>
                  <span>выход <span className={ep.exit_ok ? "text-emerald-400" : "text-rose-400"}>{ep.exit_ok ? "✓" : "✗"}</span></span>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button variant="primary" disabled={deploying} onClick={onDeploy}>
                  {deploying ? "…" : deployed ? "🔄 Пересобрать" : "🚀 Развернуть"}
                </Button>
                <Button variant="ghost" disabled={syncing} onClick={onSync}>
                  {syncing ? "…" : "Синхронизировать секреты"}
                </Button>
              </div>
            </Panel>

            <Panel title="Эффективный режим" icon="⚙️">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <KV k="Транспорт" v={TRANSPORT_LABEL[ep.transport] ?? ep.transport} />
                <KV k="Маскировка" v={cloakActive(ep) ? `ee · FakeTLS` : "dd · обфускация"} tone={cloakActive(ep) ? "emerald" : "zinc"} />
                <KV k="Порт" v={`:${ep.port}`} />
                <KV k="Реклама" v={ep.ad_tag ? `вкл · ${ep.ad_tag.slice(0, 6)}…` : ep.is_ad ? "глоб. тег" : "выкл"} tone={ep.ad_tag ? "amber" : ep.is_ad ? "amber" : "zinc"} />
                <KV k="Лимит юзеров" v={`${ep.active_subscriptions}/${ep.max_users}`} />
                <KV k="Выдача" v={ep.servable ? "вкл" : "выкл"} tone={ep.servable ? "emerald" : "amber"} />
              </div>
              {cloakActive(ep) && (
                <div className="text-[11px] text-emerald-400/70 mt-2">🔒 маскируется под <b>{ep.cloak_domain}</b></div>
              )}
              {!cloakActive(ep) && ep.cloak_domain && (
                <div className="text-[11px] text-amber-400/70 mt-2">⚠️ cloak «{ep.cloak_domain}» задан, но на dd-транспорте НЕ применяется</div>
              )}
            </Panel>

            <Panel title="Маршрут клиента" icon="🧭">
              <div className="flex items-center gap-1.5 flex-wrap text-xs">
                <RouteNode icon="👤" label="Клиент" />
                <Arrow />
                <RouteNode icon="🌐" label={ep.address} tone="brand" sub={cloakActive(ep) ? `под ${ep.cloak_domain}` : undefined} />
                {isDouble && (<><Arrow /><RouteNode icon="🌉" label={`${entryLinks.length} ${plural(entryLinks.length, ["мост", "моста", "мостов"])}`} tone="amber" /></>)}
                <Arrow />
                <RouteNode icon="🚀" label={`${exitLinks.filter((e) => e.state === "serving").length} ${plural(exitLinks.filter((e) => e.state === "serving").length, ["выход", "выхода", "выходов"])}`} tone="emerald" />
                <Arrow />
                <RouteNode icon="✈️" label="Telegram" />
              </div>
            </Panel>

            {ep.is_ad ? (
              <Panel title="Реклама (монетизация)" icon="📣">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500">ad_tag:</span>
                    {ep.ad_tag ? (
                      <code className="text-amber-300 font-mono text-xs">{ep.ad_tag.slice(0, 8)}…</code>
                    ) : (
                      <span className="text-rose-300">не задан — монетизации нет</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 leading-relaxed">
                    Ad-хост раздаёт общий доступ (без подписок). Выручку по тегу Telegram
                    начисляет на своей стороне (промо-канал) — здесь видно «что отдали»:
                    клиентов, коннекты, трафик. Замена тега — в ⚙ Настройки → «Пересобрать».
                  </div>
                </div>
              </Panel>
            ) : (
              <Panel title={`Подписки${listAll.length ? ` · ${listAll.length}` : ""}`} icon="👥">
                {listAll.length === 0 ? (
                  <div className="text-zinc-600 text-sm">Пока нет активных подписок.</div>
                ) : (
                  <>
                  {listAll.length > 5 && (
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        value={subQ}
                        onChange={(e) => setSubQ(e.target.value)}
                        placeholder="поиск по #id юзера"
                        className="flex-1 h-8 px-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-brand/40"
                      />
                      <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        {(["all", "paid", "trial"] as const).map((p) => (
                          <button
                            key={p}
                            onClick={() => setSubPlan(p)}
                            className={`px-2 py-1 rounded-md text-[11px] transition ${subPlan === p ? "bg-white/[0.08] text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}
                          >
                            {p === "all" ? "все" : p}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {list.length === 0 ? (
                    <div className="text-zinc-600 text-sm py-2">Ничего не найдено по фильтру.</div>
                  ) : (
                  <div className="rounded-lg border border-white/[0.06] overflow-hidden max-h-[28rem] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#141922]">
                        <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
                          <th className="text-left px-3 py-1.5">юзер</th>
                          <th className="text-left px-3 py-1.5">план</th>
                          <th className="text-left px-3 py-1.5">устр</th>
                          <th className="text-left px-3 py-1.5">до</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((s) => (
                          <tr key={s.id} className="border-t border-white/[0.04]">
                            <td className="px-3 py-1.5"><a href={`/users/${s.user_id}`} className="text-brand hover:underline">#{s.user_id}</a></td>
                            <td className="px-3 py-1.5"><Pill tone={KIND_TONE[s.plan] ?? "neutral"}>{s.plan}</Pill></td>
                            <td className="px-3 py-1.5 tabular-nums">{s.device_limit}</td>
                            <td className="px-3 py-1.5 text-zinc-400">{s.expires_at ? new Date(s.expires_at).toLocaleDateString("ru-RU") : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                  </>
                )}
              </Panel>
            )}
          </div>

          {/* RIGHT: трафик + пул нод */}
          <div className="space-y-3">
            <Panel title="Трафик (агрегат по нодам)" icon="📈">
              {mets.length === 0 ? (
                <div className="text-sm text-zinc-500">Агенты нод не на связи.</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-3 py-2.5">
                    <div className="text-emerald-400 text-lg font-bold">↓ {fmtBps(rxBps)}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">всего {fmtBytes(rxTot)}</div>
                  </div>
                  <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-3 py-2.5">
                    <div className="text-sky-400 text-lg font-bold">↑ {fmtBps(txBps)}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">всего {fmtBytes(txTot)}</div>
                  </div>
                  <div className="col-span-2 text-[10px] text-zinc-600 text-right">
                    {nm.isFetching ? "…" : "live"} · {mets.length} нод
                  </div>
                </div>
              )}
            </Panel>

            <Panel title="Пул нод" icon="🖧">
              <div className="space-y-1.5">
                {ep.exits.length === 0 && <div className="text-zinc-600 text-sm">Ноды не привязаны.</div>}
                {ep.exits.map((e) => {
                  const eh = health(e.health);
                  return (
                    <div key={`${e.pool_role}-${e.node_id}`} className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.05] rounded-lg px-2.5 py-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${eh.dot}`} title={eh.label} />
                      <span className="text-[10px] uppercase text-zinc-500 w-9">{e.pool_role === "entry" ? "вход" : "вых"}</span>
                      <button onClick={() => openNode(e.node_id)} className="text-sm text-zinc-200 truncate hover:text-brand hover:underline text-left" title="открыть карточку ноды">{e.name}</button>
                      <span className="ml-auto text-[11px] shrink-0 flex items-center gap-2.5">
                        {e.agent_online && e.cpu_pct != null && (
                          <span className={(e.cpu_pct >= 85 ? "text-rose-400" : e.cpu_pct >= 60 ? "text-amber-400" : "text-zinc-500")} title="загрузка ноды (loadavg)">
                            {Math.min(100, Math.round(e.cpu_pct))}%
                          </span>
                        )}
                        {e.agent_online && (e.rx_bps != null || e.tx_bps != null) && (
                          <span className="text-zinc-500" title="трафик ноды">{fmtBpsShort((e.rx_bps ?? 0) + (e.tx_bps ?? 0))}</span>
                        )}
                        {e.agent_online ? <span className="text-brand tabular-nums" title="коннектов">{e.conns ?? 0}c</span> : <span className="text-zinc-600">офл</span>}
                        {e.pool_role === "exit" && !e.primary ? (
                          <button
                            onClick={() => swap.mutate({ node_id: e.node_id, state: e.state === "serving" ? "standby" : "serving" })}
                            disabled={swap.isPending}
                            className={`px-1.5 py-0.5 rounded ${e.state === "serving" ? "text-emerald-400/80 hover:bg-white/[0.06]" : "text-zinc-500 hover:text-emerald-300 hover:bg-white/[0.06]"}`}
                            title={e.state === "serving" ? "перевести в резерв" : "сделать боевым"}
                          >
                            {stateLabel(e.state)} ⇄
                          </button>
                        ) : (
                          <span className={e.state === "serving" ? "text-emerald-400/80" : "text-zinc-500"}>{stateLabel(e.state)}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Panel>

            <Panel title="Тест-ссылка" icon="🔗">
              {ep.test_link ? (
                <button
                  onClick={() => { navigator.clipboard?.writeText(ep.test_link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  className="w-full text-left font-mono text-[11px] text-zinc-300 bg-black/30 border border-white/[0.08] rounded-lg px-3 py-2 truncate hover:border-brand/40"
                  title={ep.test_link}
                >
                  {copied ? "✓ скопировано" : ep.test_link}
                </button>
              ) : (
                <div className="text-xs text-zinc-600">нет serving-выхода с admin-секретом — разверни каскад</div>
              )}
            </Panel>

            <GeoPanel ep={ep} onChanged={() => q.refetch()} />

            <ClientsPanel endpointId={ep.id} />

            {histNodeId != null && <HistoryPanel nodeId={histNodeId} />}
          </div>
        </div>
      </div>
    </Modal>
  );
}

interface NodeMetricsLite {
  online?: boolean;
  system?: { rx_bps?: number; tx_bps?: number; rx_total?: number; tx_total?: number };
}

function RouteNode({ icon, label, sub, tone = "default" }: { icon: string; label: string; sub?: string; tone?: "default" | "brand" | "amber" | "emerald" }) {
  const c =
    tone === "brand" ? "text-brand border-brand/25 bg-brand-soft"
    : tone === "amber" ? "text-amber-300 border-amber-500/20 bg-amber-500/5"
    : tone === "emerald" ? "text-emerald-300 border-emerald-500/20 bg-emerald-500/5"
    : "text-zinc-400 border-white/[0.06] bg-white/[0.02]";
  return (
    <div className={`inline-flex flex-col items-center px-2.5 py-1.5 rounded-lg border ${c} max-w-[160px]`}>
      <span className="flex items-center gap-1.5">
        <span className="leading-none">{icon}</span>
        <span className="font-medium truncate">{label}</span>
      </span>
      {sub && <span className="text-[10px] text-zinc-600 mt-0.5 truncate">{sub}</span>}
    </div>
  );
}

function PoolBlock({
  title,
  links,
  empty,
  onAttach,
  onSwap,
  onDetach,
}: {
  title: string;
  links: ExitSummary[];
  empty: string;
  onAttach: () => void;
  onSwap: (node_id: number, state: string) => void;
  onDetach: (node_id: number) => void;
}) {
  return (
    <div className="mt-3 pt-3 border-t border-white/[0.05]">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">{title}</div>
        <button onClick={onAttach} className="text-xs text-brand hover:underline">
          + привязать
        </button>
      </div>
      {links.length === 0 ? (
        <div className="text-zinc-600 text-sm py-1">{empty}</div>
      ) : (
        <div className="space-y-1.5">
          {links.map((ex) => (
            <div
              key={ex.node_id}
              className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.05] rounded-lg px-2.5 py-1.5"
            >
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  ex.state === "serving" ? "bg-emerald-400" : "bg-zinc-600"
                }`}
                title={stateLabel(ex.state)}
              />
              <button
                onClick={() => openNode(ex.node_id)}
                className="text-sm text-zinc-200 truncate hover:text-brand hover:underline text-left"
                title="открыть карточку ноды"
              >
                {ex.name}
              </button>
              {ex.primary && (
                <span className="text-[10px] uppercase tracking-wide text-brand border border-brand/30 rounded px-1 py-0.5 shrink-0">
                  основной
                </span>
              )}
              <span className="font-mono text-xs text-zinc-600 truncate">{ex.host}</span>
              {ex.agent_online ? (
                <span className="text-[10px] flex items-center gap-1.5 shrink-0" title="система ноды: CPU · RAM · сеть · коннекты">
                  <span className="w-1 h-1 rounded-full bg-brand" />
                  {ex.cpu_pct != null && (
                    <span className={ex.cpu_pct >= 85 ? "text-rose-400" : ex.cpu_pct >= 60 ? "text-amber-400" : "text-zinc-500"} title="загрузка CPU (loadavg)">CPU {Math.min(100, Math.round(ex.cpu_pct))}%</span>
                  )}
                  {ex.mem_pct != null && (
                    <span className={ex.mem_pct >= 90 ? "text-rose-400" : ex.mem_pct >= 70 ? "text-amber-400" : "text-zinc-500"} title="память">RAM {Math.round(ex.mem_pct)}%</span>
                  )}
                  {(ex.rx_bps != null || ex.tx_bps != null) && (
                    <span className="text-zinc-500" title="сеть ↓вход ↑выход">↓{fmtBpsShort(ex.rx_bps ?? 0)} ↑{fmtBpsShort(ex.tx_bps ?? 0)}</span>
                  )}
                  <span className="text-brand" title="активных коннектов">{ex.conns ?? 0}c</span>
                </span>
              ) : (
                <span className="text-[10px] text-zinc-600 shrink-0" title="агент не на связи">нет агента</span>
              )}
              <span className={`ml-auto text-[11px] shrink-0 ${ex.state === "serving" ? "text-emerald-400/80" : "text-zinc-500"}`}>
                {stateLabel(ex.state)}
              </span>
              <div className="flex items-center gap-1">
                {!ex.primary && (
                  <button
                    onClick={() => onSwap(ex.node_id, ex.state === "serving" ? "standby" : "serving")}
                    className="text-xs px-2 py-1 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.05] transition-colors"
                  >
                    {ex.state === "serving" ? "→ в резерв" : "→ в работу"}
                  </button>
                )}
                <button
                  onClick={() => onDetach(ex.node_id)}
                  className="text-xs px-2 py-1 rounded-md text-rose-400/80 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
                >
                  отвязать
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KV({ k, v, tone = "zinc" }: { k: string; v: string; tone?: "zinc" | "emerald" | "amber" }) {
  const t: Record<string, string> = { zinc: "text-zinc-300", emerald: "text-emerald-300", amber: "text-amber-300" };
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-white/[0.04] pb-1">
      <span className="text-zinc-500">{k}</span>
      <span className={`font-medium ${t[tone]} text-right truncate`} title={v}>{v}</span>
    </div>
  );
}

function Arrow() {
  return <span className="text-zinc-700">→</span>;
}

function MenuItem({ children, onClick, danger = false }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3.5 py-2 transition-colors ${
        danger ? "text-rose-400 hover:bg-rose-500/10" : "text-zinc-300 hover:bg-white/[0.05]"
      }`}
    >
      {children}
    </button>
  );
}

function AttachExitForm({
  endpointId,
  role,
  onDone,
}: {
  endpointId: number;
  role: "entry" | "exit";
  onDone: () => void;
}) {
  const nq = useQuery<{ nodes: NodeLite[] }>({
    queryKey: ["nodes"],
    queryFn: () => api("/api/admin/nodes"),
  });
  // Занятость нод другими хостами (нода = один хост): показываем в пикере и
  // предупреждаем при выборе занятой — как в карточке редактирования.
  const epq = useQuery<{ endpoints: Endpoint[] }>({
    queryKey: ["endpoints"],
    queryFn: () => api("/api/admin/endpoints"),
  });
  const usedBy = new Map<number, string>();
  for (const e of epq.data?.endpoints ?? []) {
    if (e.id === endpointId) continue;
    if (e.entry_node_id) usedBy.set(e.entry_node_id, e.name);
    for (const x of e.exits ?? []) usedBy.set(x.node_id, e.name);
  }
  const candidates = (nq.data?.nodes ?? []).filter((n) =>
    role === "entry" ? n.role === "entry" : n.role === "exit" || n.role === "standalone",
  );
  const [nodeId, setNodeId] = useState<string>("");
  const [state, setState] = useState("serving");

  const attach = useMutation({
    mutationFn: () =>
      api(`/api/admin/endpoints/${endpointId}/exits/attach`, {
        method: "POST",
        body: JSON.stringify({ node_id: Number(nodeId), weight: 1, state, role }),
      }),
    onSuccess: onDone,
  });

  const isEntry = role === "entry";
  return (
    <div className="space-y-4">
      <Field label={isEntry ? "мост — entry-нода в РФ" : "выход — exit-нода"}>
        <NodePicker
          nodes={candidates}
          selected={nodeId ? [Number(nodeId)] : []}
          onToggle={(id) => setNodeId((cur) => (cur === String(id) ? "" : String(id)))}
          multi={false}
          usedBy={usedBy}
          emptyText={
            isEntry
              ? "Нет нод с ролью «entry». Заведите ноду-мост в разделе Ноды."
              : "Нет свободных exit-нод. Заведите ноду-выход в разделе Ноды."
          }
        />
      </Field>
      <Field label="состояние">
        <Select
          value={state}
          onChange={setState}
          options={["serving", "standby"]}
          render={(s) => stateLabel(s)}
        />
      </Field>
      {attach.isError && <div className="text-xs text-red-400">{(attach.error as Error).message}</div>}
      <div className="flex justify-end pt-1">
        <Button variant="primary" disabled={!nodeId || attach.isPending} onClick={() => attach.mutate()}>
          {attach.isPending ? "…" : "Привязать"}
        </Button>
      </div>
    </div>
  );
}

function NodePicker({
  nodes,
  selected,
  onToggle,
  multi,
  emptyText,
  usedBy,
}: {
  nodes: NodeLite[];
  selected: number[];
  onToggle: (id: number) => void;
  multi: boolean;
  emptyText: string;
  usedBy?: Map<number, string>;
}) {
  const [q, setQ] = useState("");
  const [cc, setCc] = useState("");
  const countries = [...new Set(nodes.map((n) => n.country).filter(Boolean))] as string[];
  const showFilters = nodes.length > 6;
  const compact = nodes.length > 10;
  const filtered = nodes.filter(
    (n) =>
      (!q || n.name.toLowerCase().includes(q.toLowerCase()) || n.host.includes(q)) &&
      (!cc || n.country === cc),
  );

  if (nodes.length === 0) return <p className="text-xs text-amber-400/80">{emptyText}</p>;

  return (
    <div className="space-y-2">
      {showFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="поиск по имени/IP…"
            className="h-7 flex-1 min-w-[140px] px-2.5 rounded-md bg-black/25 border border-white/[0.08] text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-brand/60"
          />
          {countries.length > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCc("")}
                className={`h-7 px-2 rounded-md text-[11px] border ${cc === "" ? "bg-brand-soft text-brand border-brand/40" : "border-white/[0.08] text-zinc-400 hover:text-zinc-200"}`}
              >
                все
              </button>
              {countries.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCc(cc === c ? "" : c)}
                  className={`h-7 px-2 rounded-md text-[11px] border ${cc === c ? "bg-brand-soft text-brand border-brand/40" : "border-white/[0.08] text-zinc-400 hover:text-zinc-200"}`}
                >
                  {flag(c)} {c}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className={compact ? "flex flex-col gap-1 max-h-56 overflow-auto pr-1" : "grid grid-cols-2 gap-2"}>
        {filtered.map((n) => {
          const on = selected.includes(n.id);
          const busyHost = !on ? usedBy?.get(n.id) : undefined;
          const hs = n.health ? health(n.health) : null;
          const pick = async () => {
            if (busyHost) {
              const ok = await confirmPopup({
                title: "Нода занята другим хостом",
                body: `«${n.name}» сейчас в хосте «${busyHost}». Одна нода = один хост: если привяжешь её сюда и развернёшь, её конфиг перезапишется под этот хост, а «${busyHost}» сломается (секреты/маршрут слетят).\n\nВсё равно взять эту ноду?`,
                danger: true,
                confirmLabel: "Да, забрать ноду",
              });
              if (!ok) return;
            }
            onToggle(n.id);
          };
          return (
            <button
              key={n.id}
              type="button"
              onClick={pick}
              title={busyHost ? `Нода занята хостом «${busyHost}». Одна нода = один хост.` : undefined}
              className={`text-left rounded-lg border transition-all ${compact ? "px-2.5 py-1.5" : "px-3 py-2"} ${
                on
                  ? "bg-brand-soft text-brand border-brand/40 glow-ring"
                  : busyHost
                    ? "bg-amber-500/[0.06] border-amber-500/25 text-zinc-300 hover:bg-amber-500/[0.1]"
                    : "bg-black/20 border-white/[0.08] text-zinc-300 hover:bg-white/[0.04]"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    on ? "bg-brand" : hs ? hs.dot : "bg-zinc-600"
                  } ${multi ? "" : "rounded-sm"}`}
                />
                <span className="truncate">{n.country ? `${flag(n.country)} ` : ""}{n.name}</span>
                {on && !multi && <span className="ml-auto text-[10px] text-brand">выбран</span>}
                {busyHost && (
                  <span className="ml-auto text-[10px] text-amber-400/90 shrink-0" title={`занята хостом ${busyHost}`}>
                    🔒 занята: {busyHost}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-zinc-500 font-mono truncate">{n.host}</span>
                {n.engine && (
                  <span className={`ml-auto text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ${
                    n.engine === "mtproto_docker" ? "bg-sky-500/15 text-sky-300" : "bg-white/[0.06] text-zinc-400"
                  }`}>
                    {ENGINE_SHORT[n.engine] ?? n.engine}
                  </span>
                )}
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && <div className="text-xs text-zinc-600 col-span-2">Ничего не найдено.</div>}
      </div>
    </div>
  );
}

function DomainStatus({ endpointId }: { endpointId: number }) {
  const q = useQuery<{
    is_domain: boolean;
    dns_ip?: string | null;
    entry_ip?: string | null;
    bound?: boolean;
    matches?: boolean;
  }>({
    queryKey: ["domain-status", endpointId],
    queryFn: () => api(`/api/admin/endpoints/${endpointId}/domain-status`),
    refetchInterval: 15000,
  });
  const d = q.data;
  if (!d) return <div className="text-[11px] text-zinc-600 mt-1">проверяю домен…</div>;
  if (!d.is_domain) return <div className="text-[11px] text-zinc-600 mt-1">address — это IP, привязка не нужна</div>;
  if (!d.bound)
    return (
      <div className="text-[11px] text-rose-300/90 mt-1">
        ✗ домен не привязан{d.entry_ip ? ` — должен указывать на ${d.entry_ip} (мост)` : ""}
      </div>
    );
  if (d.matches)
    return <div className="text-[11px] text-emerald-300/90 mt-1">✓ домен привязан → {d.dns_ip} (мост)</div>;
  return (
    <div className="text-[11px] text-amber-300/90 mt-1">
      ⚠ домен резолвится на {d.dns_ip}, а мост сейчас {d.entry_ip ?? "?"} — нажми «привязать домен»
    </div>
  );
}

function HostForm({ ep, onDone, onCancel }: { ep: Endpoint | null; onDone: () => void; onCancel: () => void }) {
  const edit = !!ep;
  const nq = useQuery<{ nodes: NodeLite[] }>({
    queryKey: ["nodes"],
    queryFn: () => api("/api/admin/nodes"),
  });
  const entries = (nq.data?.nodes ?? []).filter((n) => n.role === "entry");
  const exitNodes = (nq.data?.nodes ?? []).filter((n) => n.role === "exit" || n.role === "standalone");

  // Занятость нод другими хостами: нода = один хост (общий config.toml/порт).
  const epq = useQuery<{ endpoints: Endpoint[] }>({ queryKey: ["endpoints"], queryFn: () => api("/api/admin/endpoints") });
  const usedBy = new Map<number, string>();
  for (const e of epq.data?.endpoints ?? []) {
    if (ep && e.id === ep.id) continue;
    if (e.entry_node_id) usedBy.set(e.entry_node_id, e.name);
    for (const x of e.exits ?? []) usedBy.set(x.node_id, e.name);
  }

  const [name, setName] = useState(ep?.name ?? "");
  const [kind, setKind] = useState(ep?.kind ?? "paid");
  const [address, setAddress] = useState(ep?.address ?? "px1.nolags.pw");
  const [transport, setTransport] = useState(ep?.transport ?? "mtproxy_doublehop");
  const [cloak, setCloak] = useState(ep?.cloak_domain ?? "strm.yandex.net");
  // Режим секрета: dd (обфускация, быстро, docker-совместимо) / FakeTLS свой домен
  // (cloak = адрес хоста, как у конкурентов) / FakeTLS внешний cloak (поле ввода).
  const initSecretMode: "dd" | "own" | "cloak" = !ep
    ? "cloak"
    : !(ep.cloak_domain || "").trim()
      ? "dd"
      : (ep.cloak_domain || "").trim() === (ep.address || "").trim()
        ? "own"
        : "cloak";
  const [secretMode, setSecretMode] = useState<"dd" | "own" | "cloak">(initSecretMode);
  // Вход в форме = основной мост. Показываем entry_node_id, а если его нет —
  // первый serving-вход из пула (мост мог быть привязан через «+ привязать»).
  const initEntry =
    ep?.entry_node_id ??
    (ep?.exits ?? []).find((e) => e.pool_role === "entry")?.node_id ??
    null;
  const [entryNode, setEntryNode] = useState<string>(initEntry ? String(initEntry) : "");
  const [adTag, setAdTag] = useState(ep?.ad_tag ?? "");
  const [publicSecret, setPublicSecret] = useState(ep?.public_secret ?? "");
  const [geo, setGeo] = useState(ep?.geo_allow_countries ?? "");
  const [maxUsers, setMaxUsers] = useState(String(ep?.max_users ?? 1000));
  const initExits = (ep?.exits ?? []).filter((e) => (e.pool_role || "exit") === "exit").map((e) => e.node_id);
  const [exitSel, setExitSel] = useState<number[]>(initExits);
  const [servable, setServable] = useState(ep?.servable ?? true);

  // Пикер домена: список наших CF-зон (базовых доменов) + суб-домен → итоговый FQDN.
  const cfq = useQuery<{ accounts: { zone_id: string; name: string | null }[] }>({
    queryKey: ["cloudflare", "accounts"],
    queryFn: () => api("/api/admin/settings/cloudflare/accounts"),
  });
  const zones = (cfq.data?.accounts ?? []).map((a) => a.name).filter((n): n is string => !!n);
  const [pickSub, setPickSub] = useState("");
  const [pickZone, setPickZone] = useState("");
  const zoneVal = pickZone || zones[0] || "";
  const fqdn = (s: string, z: string) => {
    const clean = s.trim().replace(/^\.+|\.+$/g, "");
    return clean ? `${clean}.${z}` : z;
  };
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const isAd = kind === "ad";
  // dd-транспорт = обфускация без маскировки → селектор cloak прячем, cloak не пишем.
  const maskable = transport !== "mtproxy_dd";
  const body = () => ({
    name,
    kind,
    address,
    transport,
    // dd → cloak пуст; свой домен → cloak = адрес; внешний → введённый cloak.
    cloak_domain: !maskable ? null : secretMode === "dd" ? null : secretMode === "own" ? (address.trim() || null) : (cloak.trim() || null),
    entry_node_id: entryNode ? Number(entryNode) : null,
    geo_allow_countries: geo.trim() || null,
    // Пустое поле → 1000 (дефолт модели), а не 0: ноль отсекал бы выдачу целиком.
    max_users: Number(maxUsers) > 0 ? Number(maxUsers) : 1000,
    servable,
    ad_tag: adTag.trim() || null,
    ...(isAd ? { public_secret: publicSecret || null } : {}),
  });

  // Edit: PATCH хоста + доводим пул выходов (attach новых, detach убранных).
  const saveEdit = async () => {
    setErr(null);
    setBusy("save");
    try {
      await api(`/api/admin/endpoints/${ep!.id}`, { method: "PATCH", body: JSON.stringify(body()) });
      const cur = new Set(initExits);
      const next = new Set(exitSel);
      for (const nid of exitSel) {
        if (!cur.has(nid))
          await api(`/api/admin/endpoints/${ep!.id}/exits/attach`, {
            method: "POST",
            body: JSON.stringify({ node_id: nid, role: "exit", state: "serving", weight: 1 }),
          });
      }
      for (const nid of initExits) {
        if (!next.has(nid))
          await api(`/api/admin/endpoints/${ep!.id}/exits/${nid}`, { method: "DELETE" });
      }
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Новый хост: создать (с выходами атомарно) → (опц.) развернуть каскад + авто-DNS.
  const createFull = async (deploy: boolean) => {
    setErr(null);
    setBusy(deploy ? "deploy" : "create");
    try {
      const created = await api<{ id: number }>("/api/admin/endpoints", {
        method: "POST",
        body: JSON.stringify({ ...body(), exit_node_ids: exitSel }),
      });
      if (deploy) {
        const d = await api<{ ok: boolean; error?: string; steps: { node?: string; role?: string; success?: boolean; error?: string }[] }>(
          `/api/admin/endpoints/${created.id}/deploy`,
          { method: "POST" },
        );
        const lines = (d.steps ?? [])
          .map((s) => `${s.success ? "✅" : "❌"} ${s.role ?? ""} ${s.node ?? ""}${s.error ? " — " + s.error : ""}`)
          .join("\n");
        popup({
          tone: d.ok ? "success" : "warning",
          title: d.ok ? "Хост создан, каскад развёрнут" : "Хост создан, деплой с ошибками",
          body: (d.error ? d.error + "\n\n" : "") + lines,
        });
      }
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const invalid = !name.trim() || !address.trim();
  const needsEntry = transport === "mtproxy_doublehop";

  return (
    <div className="space-y-5">
      <FormSection title="Что видит клиент">
        <div className="grid grid-cols-2 gap-3">
          <Field label="имя (для админки)">
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label="тип продукта">
            <Select value={kind} onChange={setKind} options={KINDS} />
          </Field>
          <div className="col-span-2">
            <Field label="домен / IP — идёт в ссылку tg://proxy?server=…">
              {/* Поддомен шире зоны: раньше Select без ограничения забирал всю
                  ширину и поле ввода схлопывалось в нечитаемый квадратик. */}
              {zones.length > 0 && (
                <div className="flex items-stretch gap-2 mb-2">
                  <div className="flex-[3] min-w-[140px]">
                    <Input
                      value={pickSub}
                      placeholder="суб-домен (px1)"
                      onChange={(e) => { setPickSub(e.target.value); setAddress(fqdn(e.target.value, zoneVal)); }}
                      className="font-mono w-full"
                    />
                  </div>
                  <span className="self-center text-zinc-500 font-mono">.</span>
                  <div className="flex-[2] min-w-[150px]">
                    <Select
                      value={zoneVal}
                      onChange={(z) => { setPickZone(z); setAddress(fqdn(pickSub, z)); }}
                      options={zones}
                    />
                  </div>
                </div>
              )}
              <Input value={address} onChange={(e) => setAddress(e.target.value)} className="font-mono" placeholder="px1.nolags.pw" />
            </Field>
            <div className="text-[11px] mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-zinc-600">итог:</span>
              <code className="text-brand font-mono">{address.trim() || "…"}</code>
              {zones.length > 0 && (
                <span className="text-zinc-600">
                  — пиши суб-домен и выбери зону, либо впиши вручную (можно голый IP)
                </span>
              )}
            </div>
            {edit ? (
              <DomainStatus endpointId={ep!.id} />
            ) : (
              <div className="text-[11px] text-zinc-600 mt-1">
                домен привяжется автоматически при разворачивании каскада (A-запись → IP моста)
              </div>
            )}
          </div>
        </div>
      </FormSection>

      <FormSection title="Спонсорский тег (ad_tag)">
        <Field label="ad_tag — тег @MTProxybot (/newad)">
          <Input value={adTag} onChange={(e) => setAdTag(e.target.value)} placeholder={isAd ? "32 hex от @MTProxybot" : "пусто = без рекламы"} className="font-mono" />
        </Field>
        <p className="text-[11px] text-zinc-600 mt-2 leading-relaxed">
          Пишется в config выхода (middle_proxy) — при коннекте юзеру показывается спонсорский
          канал. {isAd
            ? <>Для ad-хоста — основная монетизация; пусто = подставится глобальный тег из «Настройки».</>
            : <><b className="text-zinc-400">paid/trial: пусто = без рекламы</b> (платники не видят спонсорский канал, выход идёт direct — быстрее). Задай тег, только если хочешь монетизировать и этот хост.</>}
          {" "}Замена — правишь здесь → «Пересобрать»: агент хот-рестартит выход с новым тегом, без пересоздания сервера.
        </p>
      </FormSection>

      {isAd && (
        <FormSection title="Реклама — public_secret">
          <Field label="public_secret — общий секрет для всех клиентов ad-хоста">
            <Input value={publicSecret} onChange={(e) => setPublicSecret(e.target.value)} placeholder="пусто = сгенерится" className="font-mono" />
          </Field>
          <div className="mt-2 rounded-xl bg-sky-500/[0.06] ring-1 ring-inset ring-sky-500/20 px-3 py-2 text-[11px] text-sky-200/90 leading-relaxed">
            🐳 <b>Движок выхода для рекламы:</b> ставь ноде-выходу движок{" "}
            <code className="text-sky-100">docker-mtproxy (ad)</code> — официальный
            telegrammessenger/proxy, нативный ad_tag, держит десятки тысяч коннектов. Агент сам
            поставит docker и поднимет контейнер. Движок задаётся в карточке ноды (раздел «Ноды»).
          </div>
        </FormSection>
      )}

      <FormSection title="Как маскируется">
        <div className="grid grid-cols-2 gap-3">
          <Field label="транспорт">
            <Select value={transport} onChange={setTransport} options={TRANSPORTS} render={(t) => TRANSPORT_LABEL[t] ?? t} />
          </Field>
          {maskable ? (
            <Field label="секрет / маскировка">
              <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06] w-full">
                {(
                  [
                    ["dd", "dd · быстро"],
                    ["own", "FakeTLS · свой домен"],
                    ["cloak", "FakeTLS · внешний"],
                  ] as const
                ).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setSecretMode(v)}
                    className={`flex-1 h-8 px-2 rounded-lg text-[11px] font-medium transition-all ${
                      secretMode === v ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {secretMode === "cloak" && (
                <Input value={cloak} onChange={(e) => setCloak(e.target.value)} placeholder="strm.yandex.net" className="font-mono mt-2" />
              )}
              <p className="text-[11px] text-zinc-600 mt-1.5 leading-relaxed">
                {secretMode === "dd"
                  ? "dd — обфускация без TLS. Быстрее всего (нет FakeTLS-хендшейка). Для double-hop: маскирует мост (REALITY), выход dd."
                  : secretMode === "own"
                    ? `FakeTLS под свой домен (${address || "адрес хоста"}) — SNI = сам домен, как у конкурентов. Движок с FakeTLS (zig/mtg).`
                    : "FakeTLS под внешний сайт (cloak). Движок с FakeTLS (zig/mtg)."}
              </p>
            </Field>
          ) : (
            <Field label="маскировка">
              <div className="h-8 flex items-center text-[11px] text-zinc-500">
                dd — без маскировки (обфускация, ссылка ddСЕКРЕТ)
              </div>
              <p className="text-[11px] text-zinc-600 mt-1.5 leading-relaxed">
                Быстрее всего (нет FakeTLS-хендшейка). Нужна маскировка — выбери транспорт{" "}
                <b className="text-zinc-400">FakeTLS</b> или <b className="text-zinc-400">double-hop</b>.
              </p>
            </Field>
          )}
          <Field label="лимит юзеров на хост">
            <Input
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="1000"
              className="font-mono"
              inputMode="numeric"
            />
            <p className="text-[11px] text-zinc-600 mt-1.5 leading-relaxed">
              {ep ? (
                <>
                  сейчас <b className={ep.active_subscriptions >= ep.max_users ? "text-rose-400" : "text-zinc-400"}>
                    {ep.active_subscriptions}/{ep.max_users}
                  </b>
                  {ep.active_subscriptions >= ep.max_users && " — переполнен, новые выдачи сюда не идут"}
                </>
              ) : (
                "сколько подписок принимает хост"
              )}
            </p>
          </Field>
          <div />
          <div className="col-span-2">
            <Field label="гео-лок (страны ISO через запятую) — пусто = без фильтра">
              <Input value={geo} onChange={(e) => setGeo(e.target.value)} placeholder="RU,BY,KZ" className="font-mono" />
            </Field>
            <div className="text-[11px] text-zinc-600 mt-1">
              применится на входе при «Развернуть/Пересобрать» — пускать только эти страны (остальные дропаются на файрволе)
            </div>
          </div>
        </div>
      </FormSection>

      {needsEntry && (
        <FormSection title="Вход · мост в РФ (куда коннектится клиент)">
          <NodePicker
            nodes={entries}
            selected={entryNode ? [Number(entryNode)] : []}
            onToggle={(id) => setEntryNode((cur) => (cur === String(id) ? "" : String(id)))}
            multi={false}
            usedBy={usedBy}
            emptyText="Нет entry-нод (мостов) — заведи в разделе «Ноды»."
          />
          <div className="text-[11px] text-zinc-600 mt-1.5">
            Это основной мост. <b>Несколько входов</b> (DNS round-robin, раскидывает нагрузку) —
            добавляй в карточке хоста → пул входов после создания.
          </div>
        </FormSection>
      )}

      <FormSection title="Пул выходов (можно несколько)">
        <NodePicker
          nodes={exitNodes}
          selected={exitSel}
          onToggle={(id) => setExitSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
          multi={true}
          usedBy={usedBy}
          emptyText="Нет exit-нод — заведи в разделе «Ноды»."
        />
      </FormSection>

      <label className="flex items-start gap-2.5 rounded-xl border border-white/[0.08] bg-white/[0.02] px-3.5 py-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={servable}
          onChange={(e) => setServable(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-brand shrink-0"
        />
        <span className="text-xs leading-relaxed">
          <span className="text-zinc-200 font-medium">Выдавать пользователям</span>
          <span className="text-zinc-500">
            {" "}— хост участвует в авто-выдаче. Сними галочку, чтобы поднять/протестировать
            хост, но НЕ отдавать его новым юзерам (в отличие от «Выключить» — тут хост
            остаётся живым и по тест-ссылке работает). Переключается и в меню карточки.
          </span>
        </span>
      </label>

      <p className="text-xs text-zinc-600 leading-relaxed">
        «Создать и развернуть» соберёт хост, привяжет вход+выходы, поставит софт на ноды
        (через агента) и автоматически привяжет домен. Свапать выходы serving/standby
        можно потом в карточке хоста — без смены ссылки у юзера.
      </p>

      {err && <div className="text-xs text-red-400">{err}</div>}
      <div className="flex gap-3 pt-1 justify-end flex-wrap">
        <Button variant="ghost" onClick={onCancel} disabled={!!busy}>
          Отмена
        </Button>
        {edit ? (
          <Button variant="primary" disabled={!!busy || invalid} onClick={saveEdit}>
            {busy === "save" ? "Сохраняю…" : "Сохранить"}
          </Button>
        ) : (
          <>
            <Button variant="ghost" disabled={!!busy || invalid} onClick={() => createFull(false)}>
              {busy === "create" ? "Создаю…" : "Только создать"}
            </Button>
            <Button variant="primary" disabled={!!busy || invalid} onClick={() => createFull(true)}>
              {busy === "deploy" ? "Создаю + разворачиваю…" : "🚀 Создать и развернуть"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
